<template>
  <body data-sidebar="dark">
  <!-- Begin page -->
  <div id="layout-wrapper">
    <!-- Left Sidebar Navigation -->
    <MemberPortalNavigation />

    <!-- Main Content -->
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18"></h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><router-link to="/member-dashboard">Dashboard</router-link></li>
                    <li class="breadcrumb-item active"></li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Alerts -->
          <div v-if="alert.success" class="alert alert-success alert-dismissible fade show" role="alert">
            <i class="bx bx-check-circle me-2"></i>{{ message }}
            <button type="button" class="btn-close" @click="clearAlerts"></button>
          </div>
          <div v-if="alert.info" class="alert alert-primary alert-dismissible fade show" role="alert">
            <i class="bx bx-info-circle me-2"></i>{{ message }}
            <button type="button" class="btn-close" @click="clearAlerts"></button>
          </div>
          <div v-if="alert.failed" class="alert alert-danger alert-dismissible fade show" role="alert">
            <i class="bx bx-error-circle me-2"></i>{{ message }}
            <button type="button" class="btn-close" @click="clearAlerts"></button>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3 text-muted">Loading your profile...</p>
          </div>

          <!-- Profile Content -->
          <div v-else class="row">
            <!-- Profile Sidebar Card -->
            <div class="col-xxl-4 col-xl-5">
              <!-- Main Profile Card -->
              <div class="card profile-card">
                <div class="card-body">
                  <!-- Profile Picture Section -->
                  <div class="text-center mb-4">
                    <div class="position-relative d-inline-block">
                      <!-- Profile Photo (if exists) -->
                      <img v-if="hasProfilePhoto"
                           :src="profilePictureUrl"
                           alt="Profile Picture"
                           class="rounded-circle avatar-xl profile-picture border border-3 border-primary shadow-sm"
                           style="width: 120px; height: 120px; object-fit: cover;"
                           @error="handleImageError"
                      >
                      <!-- Default Avatar (if no photo) -->
                      <div v-else
                           class="bg-primary rounded-circle d-flex align-items-center justify-content-center border border-3 border-primary shadow-sm"
                           style="width: 120px; height: 120px;">
                        <span class="text-white fw-bold" style="font-size: 2.5rem;">{{ getInitials(profile.full_name) }}</span>
                      </div>

                      <span class="status-indicator" :class="membershipStatus"></span>
                      <button
                          class="btn btn-light btn-sm position-absolute bottom-0 end-0 rounded-circle camera-btn"
                          style="width: 35px; height: 35px; padding: 0;"
                          @click="$refs.profilePictureInput.click()"
                          title="Change Profile Picture"
                      >
                        <i class="mdi mdi-camera"></i>
                      </button>
                      <input
                          ref="profilePictureInput"
                          type="file"
                          accept="image/*"
                          style="display: none"
                          @change="handleProfilePictureUpload"
                      >
                    </div>
                    <h5 class="mt-3 mb-1">{{ profile.full_name }}</h5>
                    <p class="text-primary mb-1 fw-semibold">{{ profile.cbhi_number }}</p>
                    <p class="text-muted mb-3">{{ profile.occupation || 'CBHI Member' }}</p>

                    <!-- Status Badge -->
                    <span class="badge rounded-pill px-3 py-2" :class="statusBadgeClass">
                      <i class="bx me-1" :class="statusIcon"></i>
                      {{ capitalizeFirst(profile.status) }}
                    </span>
                  </div>

                  <!-- Membership Info -->
                  <div class="membership-info-box mb-4">
                    <div class="info-row">
                      <span class="info-label">
                        <i class="bx bx-calendar-check text-success"></i>
                        Enrolled
                      </span>
                      <span class="info-value">{{ formatDate(profile.registration_date) }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">
                        <i class="bx bx-calendar-x text-warning"></i>
                        Expires
                      </span>
                      <span class="info-value">{{ formatDate(profile.expiry_date) }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">
                        <i class="bx bx-time-five text-info"></i>
                        Days Left
                      </span>
                      <span class="info-value" :class="daysLeftClass">{{ daysUntilExpiry }} days</span>
                    </div>
                  </div>

                  <!-- Quick Info Table -->
                  <div class="table-responsive">
                    <table class="table table-borderless mb-0 quick-info-table">
                      <tbody>
                      <tr>
                        <td class="fw-medium text-muted"><i class="bx bx-phone me-2"></i>Phone:</td>
                        <td class="text-end">{{ profile.phone_number || 'Not provided' }}</td>
                      </tr>
                      <tr>
                        <td class="fw-medium text-muted"><i class="bx bx-envelope me-2"></i>Email:</td>
                        <td class="text-end">{{ profile.email_address || 'Not provided' }}</td>
                      </tr>
                      <tr>
                        <td class="fw-medium text-muted"><i class="bx bx-user me-2"></i>Gender:</td>
                        <td class="text-end">{{ capitalizeFirst(profile.gender) }}</td>
                      </tr>
                      <tr>
                        <td class="fw-medium text-muted"><i class="bx bx-group me-2"></i>Household:</td>
                        <td class="text-end">{{ profile.household_size }} members</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Action Buttons -->
                  <div class="mt-4 d-grid gap-2">
                    <button class="btn btn-soft-primary" @click="switchTab('edit-profile')">
                      <i class="mdi mdi-account-edit me-1"></i>
                      Edit Profile
                    </button>
                    <button class="btn btn-soft-warning" @click="switchTab('change-password')">
                      <i class="mdi mdi-lock-reset me-1"></i>
                      Change Password
                    </button>
                  </div>
                </div>
              </div>

              <!-- Coverage Plan Card -->
              <div class="card">
                <div class="card-header bg-primary bg-gradient">
                  <h5 class="card-title mb-0 text-white">
                    <i class="bx bx-shield-check me-2"></i>Coverage Plan
                  </h5>
                </div>
                <div class="card-body">
                  <div class="plan-info">
                    <h6 class="plan-name">{{ planInfo.plan_name || 'Standard Plan' }}</h6>
                    <p class="plan-tier text-muted mb-3">{{ planInfo.tier_name || 'Basic Coverage' }}</p>

                    <div class="plan-premium">
                      <span class="premium-label">Annual Premium</span>
                      <span class="premium-amount">ETB {{ formatCurrency(profile.annual_premium) }}</span>
                    </div>

                    <div class="coverage-features mt-3">
                      <div class="feature-item">
                        <i class="bx bx-check-circle text-success"></i>
                        <span>Outpatient Care</span>
                      </div>
                      <div class="feature-item">
                        <i class="bx bx-check-circle text-success"></i>
                        <span>Inpatient Care</span>
                      </div>
                      <div class="feature-item">
                        <i class="bx bx-check-circle text-success"></i>
                        <span>Emergency Services</span>
                      </div>
                      <div class="feature-item">
                        <i class="bx bx-check-circle text-success"></i>
                        <span>Essential Medicines</span>
                      </div>
                    </div>
                  </div>

                  <router-link to="/member-coverage" class="btn btn-outline-primary w-100 mt-3 d-none">
                    <i class="bx bx-right-arrow-alt me-1"></i>View Full Coverage
                  </router-link>
                </div>
              </div>
            </div>

            <!-- Profile Details Card -->
            <div class="col-xxl-8 col-xl-7">
              <div class="card">
                <div class="card-header">
                  <!-- Nav tabs -->
                  <ul class="nav nav-tabs card-header-tabs" role="tablist">
                    <li class="nav-item" role="presentation">
                      <a class="nav-link active" data-bs-toggle="tab" href="#profile-overview" role="tab">
                        <i class="mdi mdi-account-circle me-1"></i>
                        Overview
                      </a>
                    </li>
                    <li class="nav-item" role="presentation">
                      <a class="nav-link" data-bs-toggle="tab" href="#edit-profile" role="tab">
                        <i class="mdi mdi-account-edit me-1"></i>
                        Edit Profile
                      </a>
                    </li>
                    <li class="nav-item" role="presentation">
                      <a class="nav-link" data-bs-toggle="tab" href="#change-password" role="tab">
                        <i class="mdi mdi-lock-reset me-1"></i>
                        Security
                      </a>
                    </li>
                    <li class="nav-item" role="presentation">
                      <a class="nav-link" data-bs-toggle="tab" href="#beneficiaries-tab" role="tab">
                        <i class="mdi mdi-account-group me-1"></i>
                        Beneficiaries
                      </a>
                    </li>
                  </ul>
                </div>

                <div class="card-body">
                  <div class="tab-content">
                    <!-- Overview Tab -->
                    <div class="tab-pane fade show active" id="profile-overview" role="tabpanel">
                      <!-- Personal Information -->
                      <div class="info-section">
                        <h5 class="section-title">
                          <i class="bx bx-user text-primary me-2"></i>Personal Information
                        </h5>
                        <div class="row">
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">Full Name</label>
                            <p class="mb-0">{{ profile.full_name }}</p>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">Grandfather's Name</label>
                            <p class="mb-0">{{ profile.grandfather_name || 'Not provided' }}</p>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">Date of Birth</label>
                            <p class="mb-0">{{ formatDate(profile.date_of_birth) }}</p>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">Gender</label>
                            <p class="mb-0">{{ capitalizeFirst(profile.gender) }}</p>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">Marital Status</label>
                            <p class="mb-0">{{ capitalizeFirst(profile.marital_status) }}</p>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">National ID</label>
                            <p class="mb-0">{{ profile.national_id || 'Not provided' }}</p>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">Occupation</label>
                            <p class="mb-0">{{ profile.occupation || 'Not provided' }}</p>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">Monthly Income</label>
                            <p class="mb-0">{{ profile.monthly_income ? 'ETB ' + formatCurrency(profile.monthly_income) : 'Not provided' }}</p>
                          </div>
                        </div>
                      </div>

                      <!-- Contact Information -->
                      <div class="info-section mt-4">
                        <h5 class="section-title">
                          <i class="bx bx-phone text-primary me-2"></i>Contact Information
                        </h5>
                        <div class="row">
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">Phone Number</label>
                            <p class="mb-0">{{ profile.phone_number || 'Not provided' }}</p>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">Email Address</label>
                            <p class="mb-0">{{ profile.email_address || 'Not provided' }}</p>
                          </div>
                        </div>
                      </div>

                      <!-- Address Information -->
                      <div class="info-section mt-4">
                        <h5 class="section-title">
                          <i class="bx bx-map text-primary me-2"></i>Address Information
                        </h5>
                        <div class="row">
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">Region</label>
                            <p class="mb-0">{{ profile.region_name || 'Not provided' }}</p>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">Zone</label>
                            <p class="mb-0">{{ profile.zone_name || 'Not provided' }}</p>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">Woreda</label>
                            <p class="mb-0">{{ profile.woreda_name || 'Not provided' }}</p>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">Kebele</label>
                            <p class="mb-0">{{ profile.kebele_name || 'Not provided' }}</p>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">Gote</label>
                            <p class="mb-0">{{ profile.gote || 'Not provided' }}</p>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">Address</label>
                            <p class="mb-0">{{ profile.address || 'Not provided' }}</p>
                          </div>
                        </div>
                      </div>

                      <!-- Membership Details -->
                      <div class="info-section mt-4">
                        <h5 class="section-title">
                          <i class="bx bx-id-card text-primary me-2"></i>Membership Details
                        </h5>
                        <div class="row">
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">CBHI Number</label>
                            <p class="mb-0 fw-semibold text-primary">{{ profile.cbhi_number }}</p>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">Membership Type</label>
                            <p class="mb-0">{{ formatMembershipType(profile.membership_type) }}</p>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">Registration Date</label>
                            <p class="mb-0">{{ formatDate(profile.registration_date) }}</p>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">Renewal Date</label>
                            <p class="mb-0">{{ formatDate(profile.renewal_date) || 'Not renewed yet' }}</p>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">Expiry Date</label>
                            <p class="mb-0" :class="expiryDateClass">{{ formatDate(profile.expiry_date) }}</p>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">Card Issued</label>
                            <p class="mb-0">
                              <span v-if="profile.cbhi_card_issued" class="badge bg-success">
                                <i class="bx bx-check me-1"></i>Yes
                              </span>
                              <span v-else class="badge bg-warning">
                                <i class="bx bx-time me-1"></i>Pending
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>

                      <!-- Health Information -->
                      <div class="info-section mt-4" v-if="healthProfile">
                        <h5 class="section-title">
                          <i class="bx bx-heart text-primary me-2"></i>Health Information
                        </h5>
                        <div class="row">
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">Blood Group</label>
                            <p class="mb-0">{{ healthProfile.blood_group || 'Not provided' }}</p>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">Allergies</label>
                            <p class="mb-0">{{ healthProfile.allergies || 'None reported' }}</p>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">Chronic Conditions</label>
                            <p class="mb-0">{{ healthProfile.chronic_conditions || 'None reported' }}</p>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label fw-medium text-muted">Last Health Check</label>
                            <p class="mb-0">{{ formatDate(healthProfile.last_health_check_date) || 'Not recorded' }}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Edit Profile Tab -->
                    <div class="tab-pane fade" id="edit-profile" role="tabpanel">
                      <form @submit.prevent="updateProfile">
                        <!-- Personal Information -->
                        <h5 class="section-title mb-3">
                          <i class="bx bx-user text-primary me-2"></i>Personal Information
                        </h5>
                        <div class="row">
                          <div class="col-md-6 mb-3">
                            <label for="fullName" class="form-label">Full Name <span class="text-danger">*</span></label>
                            <input type="text" class="form-control" id="fullName" v-model="editForm.full_name" required>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label for="grandfatherName" class="form-label">Grandfather's Name</label>
                            <input type="text" class="form-control" id="grandfatherName" v-model="editForm.grandfather_name">
                          </div>
                          <div class="col-md-6 mb-3">
                            <label for="dateOfBirth" class="form-label">Date of Birth</label>
                            <input type="date" class="form-control" id="dateOfBirth" v-model="editForm.date_of_birth">
                          </div>
                          <div class="col-md-6 mb-3">
                            <label for="gender" class="form-label">Gender</label>
                            <select class="form-select" id="gender" v-model="editForm.gender">
                              <option value="">Select Gender</option>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                            </select>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label for="maritalStatus" class="form-label">Marital Status</label>
                            <select class="form-select" id="maritalStatus" v-model="editForm.marital_status">
                              <option value="">Select Status</option>
                              <option value="single">Single</option>
                              <option value="married">Married</option>
                              <option value="divorced">Divorced</option>
                              <option value="widowed">Widowed</option>
                            </select>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label for="nationalId" class="form-label">National ID</label>
                            <input type="text" class="form-control" id="nationalId" v-model="editForm.national_id">
                          </div>
                          <div class="col-md-6 mb-3">
                            <label for="occupation" class="form-label">Occupation</label>
                            <input type="text" class="form-control" id="occupation" v-model="editForm.occupation">
                          </div>
                          <div class="col-md-6 mb-3">
                            <label for="monthlyIncome" class="form-label">Monthly Income (ETB)</label>
                            <input type="number" class="form-control" id="monthlyIncome" v-model="editForm.monthly_income">
                          </div>
                        </div>

                        <!-- Contact Information -->
                        <h5 class="section-title mb-3 mt-4">
                          <i class="bx bx-phone text-primary me-2"></i>Contact Information
                        </h5>
                        <div class="row">
                          <div class="col-md-6 mb-3">
                            <label for="phoneNumber" class="form-label">Phone Number <span class="text-danger">*</span></label>
                            <input type="tel" class="form-control" id="phoneNumber" v-model="editForm.phone_number" required>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label for="emailAddress" class="form-label">Email Address</label>
                            <input type="email" class="form-control" id="emailAddress" v-model="editForm.email_address">
                          </div>
                        </div>

                        <!-- Address Information -->
                        <h5 class="section-title mb-3 mt-4">
                          <i class="bx bx-map text-primary me-2"></i>Address Information
                        </h5>
                        <div class="row">
                          <div class="col-md-6 mb-3">
                            <label for="gote" class="form-label">Gote</label>
                            <input type="text" class="form-control" id="gote" v-model="editForm.gote">
                          </div>
                          <div class="col-md-6 mb-3">
                            <label for="address" class="form-label">Address</label>
                            <input type="text" class="form-control" id="address" v-model="editForm.address">
                          </div>
                        </div>

                        <!-- Health Information -->
                        <h5 class="section-title mb-3 mt-4">
                          <i class="bx bx-heart text-primary me-2"></i>Health Information
                        </h5>
                        <div class="row">
                          <div class="col-md-6 mb-3">
                            <label for="bloodGroup" class="form-label">Blood Group</label>
                            <select class="form-select" id="bloodGroup" v-model="editForm.blood_group">
                              <option value="">Select Blood Group</option>
                              <option value="A+">A+</option>
                              <option value="A-">A-</option>
                              <option value="B+">B+</option>
                              <option value="B-">B-</option>
                              <option value="AB+">AB+</option>
                              <option value="AB-">AB-</option>
                              <option value="O+">O+</option>
                              <option value="O-">O-</option>
                            </select>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label for="allergies" class="form-label">Allergies</label>
                            <input type="text" class="form-control" id="allergies" v-model="editForm.allergies" placeholder="List any known allergies">
                          </div>
                          <div class="col-md-12 mb-3">
                            <label for="chronicConditions" class="form-label">Chronic Conditions</label>
                            <textarea class="form-control" id="chronicConditions" rows="2" v-model="editForm.chronic_conditions" placeholder="List any chronic conditions"></textarea>
                          </div>
                        </div>

                        <div class="text-end mt-4">
                          <button type="button" class="btn btn-light me-2" @click="resetEditForm">Cancel</button>
                          <button type="submit" class="btn btn-primary" :disabled="updating">
                            <span v-if="updating" class="spinner-border spinner-border-sm me-2"></span>
                            {{ updating ? 'Updating...' : 'Save Changes' }}
                          </button>
                        </div>
                      </form>
                    </div>

                    <!-- Change Password Tab -->
                    <div class="tab-pane fade" id="change-password" role="tabpanel">
                      <div class="row">
                        <div class="col-lg-8">
                          <form @submit.prevent="changePassword">
                            <h5 class="section-title mb-4">
                              <i class="bx bx-lock-alt text-primary me-2"></i>Change Password
                            </h5>

                            <div class="mb-3">
                              <label for="currentPassword" class="form-label">Current Password <span class="text-danger">*</span></label>
                              <div class="input-group">
                                <input
                                    :type="showCurrentPassword ? 'text' : 'password'"
                                    class="form-control"
                                    id="currentPassword"
                                    v-model="passwordForm.currentPassword"
                                    required
                                >
                                <button type="button" class="btn btn-outline-secondary" @click="showCurrentPassword = !showCurrentPassword">
                                  <i :class="showCurrentPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                                </button>
                              </div>
                            </div>

                            <div class="mb-3">
                              <label for="newPassword" class="form-label">New Password <span class="text-danger">*</span></label>
                              <div class="input-group">
                                <input
                                    :type="showNewPassword ? 'text' : 'password'"
                                    class="form-control"
                                    id="newPassword"
                                    v-model="passwordForm.newPassword"
                                    required
                                    minlength="8"
                                >
                                <button type="button" class="btn btn-outline-secondary" @click="showNewPassword = !showNewPassword">
                                  <i :class="showNewPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                                </button>
                              </div>
                              <small class="form-text text-muted">Password must be at least 8 characters long</small>
                            </div>

                            <div class="mb-4">
                              <label for="confirmPassword" class="form-label">Confirm New Password <span class="text-danger">*</span></label>
                              <div class="input-group">
                                <input
                                    :type="showConfirmPassword ? 'text' : 'password'"
                                    class="form-control"
                                    id="confirmPassword"
                                    v-model="passwordForm.confirmPassword"
                                    required
                                    minlength="8"
                                >
                                <button type="button" class="btn btn-outline-secondary" @click="showConfirmPassword = !showConfirmPassword">
                                  <i :class="showConfirmPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                                </button>
                              </div>
                              <small v-if="passwordMismatch" class="form-text text-danger">
                                Passwords do not match
                              </small>
                            </div>

                            <div class="alert alert-info">
                              <h6 class="alert-heading"><i class="bx bx-info-circle me-2"></i>Password Requirements:</h6>
                              <ul class="mb-0 ps-3">
                                <li>At least 8 characters long</li>
                                <li>Should contain a mix of letters and numbers</li>
                                <li>Cannot be the same as your current password</li>
                              </ul>
                            </div>

                            <div class="text-end">
                              <button type="button" class="btn btn-light me-2" @click="resetPasswordForm">Cancel</button>
                              <button type="submit" class="btn btn-warning" :disabled="changingPassword || passwordMismatch">
                                <span v-if="changingPassword" class="spinner-border spinner-border-sm me-2"></span>
                                {{ changingPassword ? 'Changing...' : 'Change Password' }}
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>
                    </div>

                    <!-- Beneficiaries Tab -->
                    <div class="tab-pane fade" id="beneficiaries-tab" role="tabpanel">
                      <div class="d-flex justify-content-between align-items-center mb-4">
                        <h5 class="section-title mb-0">
                          <i class="bx bx-group text-primary me-2"></i>My Beneficiaries
                        </h5>
                        <router-link to="/beneficiaries-add" class="btn btn-primary btn-sm d-none">
                          <i class="bx bx-plus me-1"></i>Add Beneficiary
                        </router-link>
                      </div>

                      <!-- Loading beneficiaries -->
                      <div v-if="loadingBeneficiaries" class="text-center py-4">
                        <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                        <p class="mt-2 mb-0 text-muted small">Loading beneficiaries...</p>
                      </div>

                      <!-- No beneficiaries -->
                      <div v-else-if="beneficiaries.length === 0" class="text-center py-5">
                        <i class="bx bx-user-plus text-muted" style="font-size: 4rem;"></i>
                        <h6 class="mt-3">No Beneficiaries Yet</h6>
                        <p class="text-muted d-none">Add your family members to extend your coverage</p>
                        <router-link to="/beneficiaries-add" class="btn btn-primary d-none">
                          <i class="bx bx-plus me-1"></i>Add Beneficiary
                        </router-link>
                      </div>

                      <!-- Beneficiaries list -->
                      <div v-else class="beneficiaries-list">
                        <div class="row">
                          <div class="col-md-6 mb-3" v-for="beneficiary in beneficiaries" :key="beneficiary.beneficiary_id">
                            <div class="beneficiary-card">
                              <div class="beneficiary-avatar">
                                <img v-if="beneficiaryHasPhoto(beneficiary)"
                                     :src="getBeneficiaryPhotoUrl(beneficiary)"
                                     alt=""
                                     class="rounded-circle"
                                     @error="onBeneficiaryImageError($event, beneficiary)">
                                <div v-else class="avatar-placeholder">
                                  {{ getInitials(beneficiary.full_name) }}
                                </div>
                              </div>
                              <div class="beneficiary-info">
                                <h6 class="mb-1">{{ beneficiary.full_name }}</h6>
                                <p class="text-muted small mb-1">{{ beneficiary.relationship }}</p>
                                <p class="text-muted small mb-0">
                                  <i class="bx bx-calendar me-1"></i>{{ formatDate(beneficiary.date_of_birth) }}
                                </p>
                              </div>
                              <div class="beneficiary-status">
                                <span class="badge" :class="beneficiary.status === 'active' ? 'bg-success' : 'bg-warning'">
                                  {{ capitalizeFirst(beneficiary.status) }}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div class="text-center mt-3 d-none">
                          <router-link to="/beneficiaries-list" class="btn btn-outline-primary btn-sm">
                            View All Beneficiaries
                          </router-link>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
  </body>
</template>

<script>
/* eslint-disable */
import MemberPortalNavigation from "@/components/member-portal/MemberPortalNavigation.vue";
import { authUrl, memberUrl, base, executeJson } from "@/api";

export default {
  name: "MemberProfile",
  components: {
    MemberPortalNavigation
  },

  data() {
    return {
      loading: false,
      updating: false,
      changingPassword: false,
      loadingBeneficiaries: false,

      // Profile data
      profile: {},
      healthProfile: {},
      planInfo: {},
      beneficiaries: [],

      // Additional data from getMyMemberProfile
      beneficiaryCount: 0,
      lastPayment: null,
      totalPaidThisYear: 0,

      // Edit form
      editForm: {
        full_name: '',
        grandfather_name: '',
        date_of_birth: '',
        gender: '',
        marital_status: '',
        national_id: '',
        occupation: '',
        monthly_income: '',
        phone_number: '',
        email_address: '',
        gote: '',
        address: '',
        blood_group: '',
        allergies: '',
        chronic_conditions: ''
      },

      // Password form
      passwordForm: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },

      // Password visibility
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,

      // Alert states
      alert: {
        success: false,
        info: false,
        failed: false
      },
      message: ''
    };
  },

  computed: {
    // Photo URL - matching staff MemberDetails.vue pattern
    profilePictureUrl() {
      if (this.profile.profile_photo) {
        // If it's already a full URL, use it directly
        if (this.profile.profile_photo.startsWith('http')) {
          return this.profile.profile_photo;
        }
        // Otherwise, construct the full URL to member photos
        return `https://rcmsuat.craftsilicon.com/api1/uploads/member-photos/${this.profile.profile_photo}`;
      }
      return './assets/images/users/default-avatar.png';
    },

    // Check if member has profile photo
    hasProfilePhoto() {
      return this.profile && this.profile.profile_photo;
    },

    membershipStatus() {
      if (this.profile.status === 'active') return 'active';
      if (this.profile.status === 'suspended') return 'suspended';
      return 'inactive';
    },

    statusBadgeClass() {
      const statusClasses = {
        'active': 'bg-success',
        'inactive': 'bg-secondary',
        'suspended': 'bg-danger',
        'expired': 'bg-warning',
        'archived': 'bg-dark'
      };
      return statusClasses[this.profile.status] || 'bg-secondary';
    },

    statusIcon() {
      const icons = {
        'active': 'bx-check-circle',
        'inactive': 'bx-x-circle',
        'suspended': 'bx-error',
        'expired': 'bx-time',
        'archived': 'bx-archive'
      };
      return icons[this.profile.status] || 'bx-info-circle';
    },

    daysUntilExpiry() {
      if (!this.profile.expiry_date) return 0;
      const today = new Date();
      const expiry = new Date(this.profile.expiry_date);
      const diffTime = expiry - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    },

    daysLeftClass() {
      if (this.daysUntilExpiry <= 30) return 'text-danger fw-bold';
      if (this.daysUntilExpiry <= 60) return 'text-warning fw-bold';
      return 'text-success fw-bold';
    },

    expiryDateClass() {
      if (this.daysUntilExpiry <= 30) return 'text-danger';
      if (this.daysUntilExpiry <= 60) return 'text-warning';
      return '';
    },

    passwordMismatch() {
      return this.passwordForm.newPassword &&
          this.passwordForm.confirmPassword &&
          this.passwordForm.newPassword !== this.passwordForm.confirmPassword;
    }
  },

  mounted() {
    this.loadMemberProfile();
    this.loadBeneficiaries();
  },

  methods: {
    // Alert methods
    clearAlerts() {
      this.alert.success = false;
      this.alert.info = false;
      this.alert.failed = false;
    },

    showAlert(type, msg) {
      this.clearAlerts();
      this.alert[type] = true;
      this.message = msg;

      if (type === 'success' || type === 'info') {
        setTimeout(() => {
          this.clearAlerts();
        }, 5000);
      }
    },

    // Load member profile - using getMyMemberProfile for member portal
    async loadMemberProfile() {
      this.loading = true;

      const data = {
        function: "getMyMemberProfile"
      };

      try {
        const res = await executeJson(data, memberUrl);

        if (res.data.success) {
          this.profile = res.data.data.profile;
          this.healthProfile = res.data.data.healthProfile || {};
          this.planInfo = res.data.data.planInfo || {};
          this.beneficiaryCount = res.data.data.beneficiaryCount || 0;
          this.lastPayment = res.data.data.lastPayment || null;
          this.totalPaidThisYear = res.data.data.totalPaidThisYear || 0;
          this.populateEditForm();
        } else {
          this.showAlert('failed', res.data.message || 'Failed to load profile');
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        this.showAlert('failed', 'An error occurred while loading profile');
      } finally {
        this.loading = false;
      }
    },

    // Load beneficiaries - using getMyBeneficiaries for member portal
    async loadBeneficiaries() {
      this.loadingBeneficiaries = true;

      const data = {
        function: "getMyBeneficiaries"
      };

      try {
        const res = await executeJson(data, memberUrl);

        if (res.data.success) {
          this.beneficiaries = res.data.data.beneficiaries || [];
        }
      } catch (error) {
        console.error('Error loading beneficiaries:', error);
      } finally {
        this.loadingBeneficiaries = false;
      }
    },

    // Populate edit form
    populateEditForm() {
      this.editForm = {
        full_name: this.profile.full_name || '',
        grandfather_name: this.profile.grandfather_name || '',
        date_of_birth: this.profile.date_of_birth || '',
        gender: this.profile.gender || '',
        marital_status: this.profile.marital_status || '',
        national_id: this.profile.national_id || '',
        occupation: this.profile.occupation || '',
        monthly_income: this.profile.monthly_income || '',
        phone_number: this.profile.phone_number || '',
        email_address: this.profile.email_address || '',
        gote: this.profile.gote || '',
        address: this.profile.address || '',
        blood_group: this.healthProfile.blood_group || '',
        allergies: this.healthProfile.allergies || '',
        chronic_conditions: this.healthProfile.chronic_conditions || ''
      };
    },

    // Update profile - using updateMyProfile for member portal
    async updateProfile() {
      this.updating = true;

      const data = {
        function: "updateMyProfile",
        ...this.editForm
      };

      try {
        const res = await executeJson(data, memberUrl);

        if (res.data.success) {
          this.showAlert('success', 'Profile updated successfully');
          this.loadMemberProfile();
          this.switchTab('profile-overview');
        } else {
          this.showAlert('failed', res.data.message || 'Failed to update profile');
        }
      } catch (error) {
        console.error('Error updating profile:', error);
        this.showAlert('failed', 'An error occurred while updating profile');
      } finally {
        this.updating = false;
      }
    },

    // Change password - using changeMyPassword for member portal
    async changePassword() {
      if (this.passwordMismatch) {
        this.showAlert('failed', 'Passwords do not match');
        return;
      }

      this.changingPassword = true;

      const data = {
        function: "changeMyPassword",
        currentPassword: this.passwordForm.currentPassword,
        newPassword: this.passwordForm.newPassword
      };

      try {
        const res = await executeJson(data, memberUrl);

        if (res.data.success) {
          this.showAlert('success', 'Password changed successfully');
          this.resetPasswordForm();
          this.switchTab('profile-overview');
        } else {
          this.showAlert('failed', res.data.message || 'Failed to change password');
        }
      } catch (error) {
        console.error('Error changing password:', error);
        this.showAlert('failed', 'An error occurred while changing password');
      } finally {
        this.changingPassword = false;
      }
    },

    // Handle profile picture upload - using uploadMyPhoto for member portal
    async handleProfilePictureUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.showAlert('failed', 'File size must be less than 5MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        this.showAlert('failed', 'Please select a valid image file');
        return;
      }

      const formData = new FormData();
      formData.append('function', 'uploadMyPhoto');
      formData.append('photo', file);

      try {
        const response = await fetch(memberUrl, {
          method: 'POST',
          body: formData
        });

        const res = await response.json();

        if (res.success) {
          this.profile.profile_photo = res.data.profilePhoto;
          this.showAlert('success', 'Profile picture updated successfully');
        } else {
          this.showAlert('failed', res.message || 'Failed to upload profile picture');
        }
      } catch (error) {
        console.error('Error uploading photo:', error);
        this.showAlert('failed', 'An error occurred while uploading photo');
      }
    },

    // Reset forms
    resetEditForm() {
      this.populateEditForm();
    },

    resetPasswordForm() {
      this.passwordForm = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      };
      this.showCurrentPassword = false;
      this.showNewPassword = false;
      this.showConfirmPassword = false;
    },

    // Switch tab
    switchTab(tabName) {
      const tabElement = document.querySelector(`a[href="#${tabName}"]`);
      if (tabElement && typeof bootstrap !== 'undefined') {
        const tab = new bootstrap.Tab(tabElement);
        tab.show();
      }
    },

    // Utility methods
    handleImageError(event) {
      console.error('Failed to load image:', event.target.src);
      // Hide the broken image - v-if will then show the default avatar
      event.target.style.display = 'none';
      // Reset profile_photo to trigger v-else default avatar
      this.profile.profile_photo = null;
    },

    getPhotoUrl(photo) {
      if (!photo) return './assets/images/users/default-avatar.png';
      if (photo.startsWith('http')) return photo;
      return `https://rcmsuat.craftsilicon.com/api1/uploads/member-photos/${photo}`;
    },

    // Get beneficiary photo URL
    getBeneficiaryPhotoUrl(beneficiary) {
      if (beneficiary.profile_photo) {
        if (beneficiary.profile_photo.startsWith('http')) {
          return beneficiary.profile_photo;
        }
        return `https://api.craftcollect.africa/cbhi/uploads/beneficiary-photos/${beneficiary.profile_photo}`;
      }
      return null;
    },

    // Check if beneficiary has photo
    beneficiaryHasPhoto(beneficiary) {
      return beneficiary && beneficiary.profile_photo;
    },

    // Handle beneficiary image error
    onBeneficiaryImageError(event, beneficiary) {
      console.error('Failed to load beneficiary image:', event.target.src);
      event.target.style.display = 'none';
      // Reset the photo reference in the beneficiary
      const index = this.beneficiaries.findIndex(b => b.beneficiary_id === beneficiary.beneficiary_id);
      if (index !== -1) {
        this.beneficiaries[index].profile_photo = null;
      }
    },

    getInitials(name) {
      if (!name) return '?';
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },

    formatDate(date) {
      if (!date) return 'Not provided';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    formatCurrency(amount) {
      if (!amount) return '0';
      return new Intl.NumberFormat().format(amount);
    },

    capitalizeFirst(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1).replace(/_/g, ' ');
    },

    formatMembershipType(type) {
      if (!type) return 'Member';
      const types = {
        'household_head': 'Household Head',
        'individual': 'Individual Member'
      };
      return types[type] || type;
    }
  }
};
</script>

<style scoped>
/* Profile Card Styles */
.profile-card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border-radius: 0.75rem;
}

.profile-picture {
  border: 4px solid #2c5aa0;
  box-shadow: 0 4px 15px rgba(44, 90, 160, 0.2);
  transition: all 0.3s ease;
}

.profile-picture:hover {
  transform: scale(1.02);
  box-shadow: 0 6px 20px rgba(44, 90, 160, 0.3);
}

.camera-btn {
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  z-index: 10;
}

.camera-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.status-indicator {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 3px solid #fff;
}

.status-indicator.active {
  background-color: #28a745;
}

.status-indicator.inactive {
  background-color: #6c757d;
}

.status-indicator.suspended {
  background-color: #dc3545;
}

.avatar-xl {
  width: 120px;
  height: 120px;
  object-fit: cover;
}

/* Membership Info Box */
.membership-info-box {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 0.75rem;
  padding: 1rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
}

.info-row:not(:last-child) {
  border-bottom: 1px dashed #dee2e6;
}

.info-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6c757d;
  font-size: 0.875rem;
}

.info-value {
  font-weight: 600;
  color: #495057;
}

/* Quick Info Table */
.quick-info-table td {
  padding: 0.5rem 0;
  font-size: 0.9rem;
}

/* Plan Info */
.plan-info {
  text-align: center;
}

.plan-name {
  color: #2c5aa0;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.plan-premium {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-radius: 0.75rem;
  padding: 1rem;
  margin-top: 1rem;
}

.premium-label {
  display: block;
  color: #6c757d;
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
}

.premium-amount {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c5aa0;
}

.coverage-features {
  text-align: left;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0;
  font-size: 0.875rem;
}

/* Section Title */
.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1e3f70;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
  margin-bottom: 1rem;
}

.info-section .row {
  margin-top: 0.5rem;
}

.info-section .form-label {
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
}

.info-section p {
  font-size: 0.95rem;
  color: #495057;
}

/* Nav Tabs */
.nav-tabs .nav-link {
  color: #6c757d;
  border-bottom: 2px solid transparent;
  padding: 0.75rem 1rem;
}

.nav-tabs .nav-link:hover {
  color: #2c5aa0;
}

.nav-tabs .nav-link.active {
  color: #2c5aa0;
  border-bottom-color: #2c5aa0;
  font-weight: 600;
}

/* Beneficiary Card */
.beneficiary-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 0.75rem;
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
}

.beneficiary-card:hover {
  border-color: #2c5aa0;
  box-shadow: 0 4px 12px rgba(44, 90, 160, 0.1);
}

.beneficiary-avatar img,
.beneficiary-avatar .avatar-placeholder {
  width: 50px;
  height: 50px;
  object-fit: cover;
}

.beneficiary-avatar .avatar-placeholder {
  background: linear-gradient(135deg, #2c5aa0 0%, #4a90e2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 1rem;
}

.beneficiary-info {
  flex: 1;
}

.beneficiary-info h6 {
  color: #1e3f70;
  font-weight: 600;
}

/* Soft Buttons */
.btn-soft-primary {
  background-color: rgba(44, 90, 160, 0.1);
  color: #2c5aa0;
  border: none;
}

.btn-soft-primary:hover {
  background-color: #2c5aa0;
  color: #fff;
}

.btn-soft-warning {
  background-color: rgba(255, 193, 7, 0.1);
  color: #d39e00;
  border: none;
}

.btn-soft-warning:hover {
  background-color: #ffc107;
  color: #1e3f70;
}

/* Card Styles */
.card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border-radius: 0.75rem;
}

.card-header {
  background: #fff;
  border-bottom: 1px solid #e9ecef;
}

/* Alert Styles */
.alert {
  border: none;
  border-radius: 0.5rem;
  border-left: 4px solid;
}

.alert-success {
  border-left-color: #28a745;
}

.alert-danger {
  border-left-color: #dc3545;
}

.alert-primary {
  border-left-color: #2c5aa0;
}

/* Form Styles */
.form-control:focus,
.form-select:focus {
  border-color: #2c5aa0;
  box-shadow: 0 0 0 0.2rem rgba(44, 90, 160, 0.15);
}

/* Responsive */
@media (max-width: 768px) {
  .avatar-xl {
    width: 100px;
    height: 100px;
  }

  .nav-tabs .nav-link {
    padding: 0.5rem;
    font-size: 0.8rem;
  }

  .nav-tabs .nav-link i {
    display: none;
  }

  .beneficiary-card {
    flex-direction: column;
    text-align: center;
  }

  .beneficiary-status {
    margin-top: 0.5rem;
  }
}

/* Content area with sidebar */
.content-minimize {
  margin-left: 250px;
  transition: margin-left 0.3s ease;
}

.page-content {
  padding: 70px 0 60px 0;
  min-height: 100vh;
}

@media (max-width: 992px) {
  .content-minimize {
    margin-left: 0 !important;
  }
}

.vertical-collpsed .content-minimize {
  margin-left: 70px;
}
</style>